import { store } from '../store';
import {
  addTranscript,
  addSpeechRateAlert,
  updateCurrentSpeechRate,
  setParticipants,
  setSalespersonUnresolved,
  setRosterEditNotice,
  Participant,
  ParticipantAssignment,
} from '../store/conversationSlice';
import { sessionManager } from './sessionManager';
import { LiveReconnectScheduler } from './liveReconnect';
import { participantIdOf } from '../utils/participantId';
import { validateAndRefreshToken } from '../utils/tokenUtils';
import { ROSTER_COPY, ROSTER_REFUSAL_WINDOW_MS } from '../constants/participantRoster';
import { parseParticipantRoleChange } from '../utils/participantRoleChange';
import { applyParticipantRoleChange, applySnapshotDecisions } from './participantRoleChanges';

// Roster entries as they arrive on the wire. Recall participant ids are raw
// integers server-side, so ids may arrive as numbers — coerce on receipt.
interface WireParticipant {
  participantId?: string | number;
  name?: string;
  role?: string;
  source?: string;
  externalId?: string | number;
  /** Recall's or the calendar invite's email for this participant (services 5d59edf); null = unknown. */
  email?: string | null;
  /** The bot owner confirmed this entry (on any device). */
  confirmed?: boolean;
  /** When (services epoch ms); null/absent on an unconfirmed entry or from an older service. */
  confirmedAt?: number | null;
}

interface RecallTranscript {
  type: string;
  bot_id?: string;
  data?: {
    text?: string;
    speaker?: string;
    speaker_id?: string | number;
    speaker_name?: string;
    is_host?: boolean;
    timestamp?: string;
    // participants_update / salesperson_unresolved
    participants?: WireParticipant[];
    // participant_reclassified (the in-place shape; parsed defensively)
    participantId?: string | number | null;
    name?: string | null;
    role?: string | null;
    confirmed?: boolean;
    confirmedAt?: number | null;
    // Speech rate fields
    total_words?: number;
    max_words?: number;
    /** The WPM threshold, once services carrying it are deployed. */
    wpm_threshold?: number;
    time_window?: number;
    words_per_minute?: number;
    is_too_fast?: boolean;
    // speech_rate_alert: `participantId` (above) is the speaker it is about (services 2026-10-05).
    // error (e.g. a refused roster edit)
    message?: string;
    // session_ended
    room_id?: string;
    /** Older shape; the contract puts `reason` at the top level. */
    reason?: string;
    /** session_ended: the bot ended without ever being let in (as on calendar bot_terminated). */
    notAdmitted?: boolean;
    /** session_ended: why it was never let in (constants/botAdmission.ts), or null. */
    notAdmittedReason?: string | null;
  };
  /** session_ended: why the session ended (REALTIME_ARCHITECTURE.md §10). */
  reason?: SessionEndedReason;
  error?: string;
}

/** `session_ended.reason` values in the cross-app contract. */
export type SessionEndedReason = 'user' | 'idle_timeout' | 'server';

/** The bot's session ended server-side (calendar_sync finalized it). */
export interface RecallSessionEnded {
  roomId: string;
  /** Absent while the recall service does not send one yet. */
  reason?: string;
  /** True when the bot was never let in; older services omit it (a normal end). */
  notAdmitted?: boolean;
  /** Why it was never let in, or null. */
  notAdmittedReason?: string | null;
}

export type RecallSessionEndedListener = (event: RecallSessionEnded) => void;

/** The socket reopened after a drop; the argument is the bot (room) id. */
export type RecallReconnectedListener = (roomId: string) => void;

/**
 * The recall service keys its roster on the RAW participant id it got from
 * recall.ai — an integer for real meeting participants. A confirmed role sent
 * back as the string "100" would land on a phantom roster key, so all-digit
 * ids must go over the wire as JSON numbers.
 */
export const encodeParticipantId = (participantId: string): string | number =>
  /^\d+$/.test(participantId) ? Number(participantId) : participantId;

const toRosterParticipants = (participants?: WireParticipant[]): Participant[] =>
  (participants || [])
    .filter((p) => p?.participantId !== undefined && p?.participantId !== null)
    .map((p) => ({
      participantId: String(p.participantId),
      name: p.name,
      role: p.role === 'salesperson' ? 'salesperson' as const : 'customer' as const,
      source: p.source,
      externalId: p.externalId !== undefined && p.externalId !== null ? String(p.externalId) : undefined,
      // `null` means not known (yet): leave the key out, so the roster merge
      // keeps an email an earlier frame gave for this participant.
      ...(p.email ? { email: p.email } : {}),
      ...(p.confirmed === true ? { confirmed: true } : {}),
      ...(typeof p.confirmedAt === 'number' && Number.isFinite(p.confirmedAt) ? { confirmedAt: p.confirmedAt } : {}),
    }));

class RecallWebSocketService {
  private ws: WebSocket | null = null;
  private isConnecting = false;
  private botId: string | null = null;
  private websocketUrl: string | null = null;
  private connectedListeners = new Set<() => void>();

  onConnected(listener: () => void): () => void {
    this.connectedListeners.add(listener);
    return () => this.connectedListeners.delete(listener);
  }
  private sessionEndedListeners = new Set<RecallSessionEndedListener>();
  private reconnectedListeners = new Set<RecallReconnectedListener>();
  /** The current bot's socket has opened once: the next open is a reconnect. */
  private hasOpenedCurrentBot = false;
  /** When this client last sent a roster edit (see handleServerError). */
  private lastRosterEditAt: number | null = null;
  private readonly reconnect = new LiveReconnectScheduler('recall', () => this.reconnectToCurrentBot());

  async connect(botId: string, websocketUrl: string): Promise<void> {
    this.reconnect.activate();

    // Check if we're switching to a different bot
    const isSwitchingBots = this.botId && this.botId !== botId;

    if (isSwitchingBots) {
      console.log(`🔄 Switching Recall WebSocket from bot ${this.botId} to ${botId}`);
      // Force disconnect from old bot before connecting to new one
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }
      this.isConnecting = false; // Reset connecting flag to allow new connection
    }

    // Guard: Check if already connected to SAME bot to prevent duplicate connections
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN && this.botId === botId)) {
      console.log('🔌 Already connected or connecting to Recall WebSocket for same bot');
      return;
    }

    // Clean up any existing connection before creating new one
    if (this.ws) {
      console.log('🔌 Closing existing Recall WebSocket before reconnecting');
      this.ws.close();
      this.ws = null;
    }

    this.isConnecting = true;
    if (this.botId !== botId) this.hasOpenedCurrentBot = false;
    this.botId = botId;
    this.websocketUrl = websocketUrl;

    try {
      // Get room_id from sessionManager
      const roomId = sessionManager.getRoomId() || botId;

      // Always pull a fresh Firebase token at (re)connect time. Reading from
      // Redux is unsafe — Redux can lag onIdTokenChanged, and reconnects
      // during long sessions are exactly when a stale token would surface.
      const { token } = await validateAndRefreshToken();

      // Disconnected or switched bots while the token was being fetched.
      if (this.botId !== botId) {
        console.log(`⏭️ Recall connect for ${botId} superseded during token fetch`);
        return;
      }

      if (!token) {
        console.error('🔌 No Firebase token available for Recall WebSocket authentication');
        this.isConnecting = false;
        throw new Error('Authentication token required');
      }

      // Connect to the recall WebSocket service with room_id and JWT token
      const wsUrl = `${websocketUrl}?room_id=${roomId}&token=${encodeURIComponent(token)}`;
      console.log('🔌 Connecting to Recall WebSocket with authentication');

      const socket = new WebSocket(wsUrl);
      this.ws = socket;

      socket.onopen = () => {
        if (this.ws !== socket) return;
        console.log('✅ Connected to Recall WebSocket service');
        this.isConnecting = false;
        this.reconnect.markConnected();
        const isReopen = this.hasOpenedCurrentBot;
        this.hasOpenedCurrentBot = true;
        for (const listener of Array.from(this.connectedListeners)) {
          try {
            listener();
          } catch (error) {
            console.warn('Recall WebSocket connected listener failed', error);
          }
        }
        if (isReopen) this.notifyReconnected(botId);
      };

      socket.onmessage = (event) => {
        try {
          const message: RecallTranscript = JSON.parse(event.data);
          console.log('📨 Received WebSocket message:', message);
          this.handleMessage(message);
        } catch (error) {
          console.error('❌ Error parsing Recall WebSocket message:', error);
        }
      };

      socket.onerror = (error) => {
        console.error('❌ Recall WebSocket error:', error);
      };

      socket.onclose = (event) => {
        // A socket this service replaced or closed on purpose reports nothing.
        if (this.ws !== socket) return;
        console.log('🔌 Recall WebSocket connection closed:', event?.code, event?.reason);
        this.isConnecting = false;
        this.ws = null;
        // Any drop while the bot session is live (including a server-side
        // 1000 close) is retried; disconnect() is the only way to stop.
        this.reconnect.scheduleReconnect();
      };

    } catch (error) {
      console.error('❌ Failed to connect to Recall WebSocket:', error);
      this.isConnecting = false;
      // No socket was opened, so no close event will retry: retry from here
      // (a no-op once disconnect() has stopped the loop).
      this.reconnect.scheduleReconnect();
      throw error;
    }
  }

  private handleMessage(message: RecallTranscript) {
    // Skip connection established messages
    if (message.type === 'connection_established') {
      console.log('✅ Recall WebSocket connection confirmed');
      return;
    }

    // Check if message is for the currently active bot
    // If message has bot_id, verify it matches our connected bot
    if (message.bot_id && message.bot_id !== this.botId) {
      console.log(`⏭️ Ignoring message for different bot: ${message.bot_id} (active: ${this.botId})`);
      return;
    }

    // Also check against sessionManager's room_id for extra safety
    const activeRoomId = sessionManager.getRoomId();
    if (activeRoomId && message.bot_id && message.bot_id !== activeRoomId) {
      console.log(`⏭️ Ignoring message - bot_id ${message.bot_id} doesn't match active room ${activeRoomId}`);
      return;
    }

    if (message.type === 'session_ended') {
      this.handleSessionEnded(message);
      return;
    }

    if (message.type === 'error') {
      this.handleServerError(message);
      return;
    }

    // Handle participant join event - bot has joined the meeting
    if (message.type === 'participant_join') {
      console.log('🤖 Bot has joined the meeting');

      // Recall sends a join for EVERY participant: only the first starts the
      // call (a saved start from a watch / resume is kept), or the live header's
      // "Started at" and counter would reset on each join (2026-10-07).
      if (!sessionManager.getSessionInfo()?.callStartTime) sessionManager.setCallStartTime();

      // Dispatch custom event that the modal can listen for
      window.dispatchEvent(new CustomEvent('bot-joined-meeting', {
        detail: { botId: this.botId }
      }));
      return;
    }

    // Handle transcripts from Recall.ai
    if (message.type === 'transcript') {

      if (!message.data) {
        console.warn('⚠️ Transcript message has no data field');
        return;
      }

      const { text, speaker, speaker_id, speaker_name, is_host, timestamp } = message.data;

      if (!text || !text.trim()) {
        console.log('⏭️ Skipping empty transcript');
        return; // Skip empty transcripts
      }

      // Trust the backend's pre-calculated speaker role
      // The backend uses participant_id caching to prevent Recall.ai is_host inconsistencies
      let actualSpeaker: 'customer' | 'salesperson';

      if (speaker === 'customer' || speaker === 'salesperson') {
        // Use backend's cached speaker role (handles is_host inconsistencies)
        actualSpeaker = speaker;
      } else {
        // Fallback if backend sends unexpected value (shouldn't happen)
        console.warn(`⚠️ Unexpected speaker value from backend: ${speaker}, using is_host fallback`);
        const state = store.getState();
        const isSalespersonHost = state.conversation.isSalespersonHost;
        actualSpeaker = isSalespersonHost
          ? (is_host ? 'salesperson' : 'customer')
          : (is_host ? 'customer' : 'salesperson');
      }

      console.log(`📝 Recall transcript - Backend speaker: ${speaker}, Using: ${actualSpeaker}`);

      // Add transcript to Redux store for display (with per-participant identity)
      store.dispatch(addTranscript({
        text: text,
        speaker: actualSpeaker,
        participantId: speaker_id !== undefined && speaker_id !== null ? String(speaker_id) : undefined,
        speakerName: speaker_name,
        timestamp: timestamp || new Date().toISOString()
      }));

      // NOTE: Frontend no longer sends transcripts to backend services!
      // All transcript broadcasting is handled by recall_websocket_service.py (port 3456)
      // which receives transcripts from Recall.ai and forwards them to:
      //   - production_final_server.py (questions/answers)
      //   - landmine_detection_service.py (landmine detection)
      //   - competitor_detection_service.py (competitor mentions)
      //   - saver_service.py (incremental persistence)
      //
      // Frontend only:
      //   1. Receives transcripts from recall_websocket_service.py for display
      //   2. Receives processed results from backend services (questions, alerts, etc.)
      return;
    }

    // The bot owner decided who one participant is (on any device): every
    // earlier line, their concern cards and alerts follow; never re-detected.
    if (message.type === 'participant_reclassified') {
      const change = parseParticipantRoleChange(message.data);
      if (change) applyParticipantRoleChange(change);
      else console.warn('⚠️ Ignoring a participant_reclassified with no participant or change');
      return;
    }

    // Multi-party roster updates (1+ salespeople + N customers). A confirmed
    // entry newer than this app's decision is a decision it missed; any other
    // entry never changes a decided role or name (a stale snapshot).
    if (message.type === 'participants_update') {
      const roster = toRosterParticipants(message.data?.participants);
      applySnapshotDecisions(roster);
      store.dispatch(setParticipants(roster));
      return;
    }

    // Salesperson could not be auto-identified (rep is not host, no name match)
    if (message.type === 'salesperson_unresolved') {
      const roster = toRosterParticipants(message.data?.participants);
      applySnapshotDecisions(roster);
      store.dispatch(setSalespersonUnresolved(roster));
      return;
    }

    // Handle speech rate updates
    if (message.type === 'speech_rate_update') {
      store.dispatch(updateCurrentSpeechRate({
        words_per_minute: message.data?.words_per_minute || 0,
        is_too_fast: message.data?.is_too_fast || false,
      }));
      return;
    }

    // Handle speech rate alerts
    if (message.type === 'speech_rate_alert') {
      store.dispatch(addSpeechRateAlert({
        words_per_minute: message.data?.words_per_minute || 0,
        // `max_words` is the fallback while a service that predates
        // `wpm_threshold` is still broadcasting; both carry a WPM number.
        wpm_threshold: message.data?.wpm_threshold ?? message.data?.max_words ?? 190,
        timestamp: new Date().toISOString(),
        // The speaker it is about: the banner shows only the viewer's own
        // (the relay broadcasts it to everyone on the bot call).
        participantId: participantIdOf(message.data?.participantId),
      }));
      // Also update current speech rate when we get an alert
      store.dispatch(updateCurrentSpeechRate({
        words_per_minute: message.data?.words_per_minute || 0,
        is_too_fast: true,
      }));
      console.log('🎤 Speech rate alert received:', message.data);
      return;
    }
  }

  /**
   * The server finalized this bot's session. Stop for good (a reconnect would
   * re-join a dead room), then tell the page, which runs the end-of-call flow
   * and closes the other live sockets.
   */
  private handleSessionEnded(message: RecallTranscript) {
    const roomId = message.data?.room_id || message.bot_id || this.botId;
    if (!roomId || roomId !== this.botId) {
      console.log(`⏭️ Ignoring session_ended for room ${roomId} (active: ${this.botId})`);
      return;
    }

    console.log(`🏁 Recall session ended for room ${roomId}`);
    this.disconnect();
    const endedEvent: RecallSessionEnded = {
      roomId,
      reason: message.reason ?? message.data?.reason,
      notAdmitted: message.data?.notAdmitted,
      notAdmittedReason: message.data?.notAdmittedReason,
    };
    this.sessionEndedListeners.forEach((listener) => {
      try {
        listener(endedEvent);
      } catch (error) {
        console.error('❌ session_ended listener failed:', error);
      }
    });
  }

  /**
   * The server refused something this client sent. The only refusal on this
   * socket is a roster edit from someone who is not the bot's owner, and the
   * frame names no request, so it counts as one only right after a roster edit;
   * the rep then sees a short notice. Anything else is only logged.
   */
  private handleServerError(message: RecallTranscript) {
    const isRosterRefusal =
      this.lastRosterEditAt !== null && Date.now() - this.lastRosterEditAt <= ROSTER_REFUSAL_WINDOW_MS;
    this.lastRosterEditAt = null;
    console.warn('⚠️ Recall service refused a request:', message.data?.message ?? message.error);
    if (isRosterRefusal) {
      store.dispatch(setRosterEditNotice(ROSTER_COPY.editRefused));
    }
  }

  /** Called when the server ends this bot's session. Returns an unsubscribe function. */
  onSessionEnded(listener: RecallSessionEndedListener): () => void {
    this.sessionEndedListeners.add(listener);
    return () => {
      this.sessionEndedListeners.delete(listener);
    };
  }

  /**
   * Called each time the socket reopens after a drop (not on its first open):
   * the page restores the history it missed while down. Returns an
   * unsubscribe function.
   */
  onReconnected(listener: RecallReconnectedListener): () => void {
    this.reconnectedListeners.add(listener);
    return () => {
      this.reconnectedListeners.delete(listener);
    };
  }

  private notifyReconnected(roomId: string) {
    this.reconnectedListeners.forEach((listener) => {
      try {
        listener(roomId);
      } catch (error) {
        console.error('❌ Recall reconnected listener failed:', error);
      }
    });
  }

  private reconnectToCurrentBot(): Promise<void> {
    if (!this.botId || !this.websocketUrl) {
      return Promise.reject(new Error('Recall reconnect has no bot to rejoin'));
    }
    return this.connect(this.botId, this.websocketUrl);
  }

  disconnect() {
    this.reconnect.stop();
    if (this.ws) {
      console.log('🔌 Disconnecting from Recall WebSocket');
      this.ws.close();
      this.ws = null;
    }
    this.botId = null;
    this.websocketUrl = null;
    this.hasOpenedCurrentBot = false;
    this.lastRosterEditAt = null;
    // Without this, disconnecting mid-handshake leaves the guard in connect()
    // permanently tripped and every later connect() is a no-op (repro: user
    // closes bot mode while the socket is still opening).
    this.isConnecting = false;
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Send a message to the recall WebSocket service
   * Used for forwarding events to saver_service (e.g., sales playbook updates)
   */
  send(message: { type: string; data: any }): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      console.log(`📤 Sent message to recall WebSocket: ${message.type}`);
    } else {
      console.warn('⚠️ Cannot send message - recall WebSocket not connected');
    }
  }

  /**
   * Manual override: tell the backend which participant is the salesperson.
   * Backend re-tags going forward and backfills prior turns.
   */
  setSalesperson(participantId: string): void {
    this.lastRosterEditAt = Date.now();
    this.send({
      type: 'set_salesperson',
      data: { participantId: encodeParticipantId(participantId) },
    });
  }

  /**
   * Creator confirmed roles + names for the whole roster. Backend applies the
   * assignments (multiple salespeople allowed — team selling) and backfills
   * prior turns via the saver. All-digit ids go over the wire as JSON numbers
   * — see encodeParticipantId.
   */
  updateParticipants(participants: ParticipantAssignment[]): void {
    this.lastRosterEditAt = Date.now();
    this.send({
      type: 'update_participants',
      data: {
        participants: participants.map((p) => ({
          participantId: encodeParticipantId(p.participantId),
          role: p.role,
          name: p.name,
        })),
      },
    });
  }

  /**
   * Send a sales playbook update event to be forwarded to saver_service
   * @deprecated Use sendSalesPlaybookUpdate instead
   */
  sendTalkTrackUpdate(data: {
    roomId: string;
    updateType: 'selection' | 'question_asked' | 'question_note';
    playbookName?: string;
    playbookDisplayName?: string;
    role?: string;
    roleDisplayName?: string;
    totalQuestionsAvailable?: number;
    questionId?: string;
    questionText?: string;
    tags?: string[];
    isAsked?: boolean;
    note?: string;
  }): void {
    // Forward to the new method
    this.sendSalesPlaybookUpdate(data);
  }

  /**
   * Send a sales playbook update event to be forwarded to saver_service
   */
  sendSalesPlaybookUpdate(data: {
    roomId: string;
    updateType: 'selection' | 'question_asked' | 'question_note' | 'pitch_checked' | 'pitch_note' | 'advanced_checked' | 'advanced_note' | 'reset_item';
    playbookName?: string;
    playbookDisplayName?: string;
    role?: string;
    roleDisplayName?: string;
    totalQuestionsAvailable?: number;
    questionId?: string;
    questionText?: string;
    tags?: string[];
    isAsked?: boolean;
    isChecked?: boolean;
    note?: string;
    pitchId?: string;
    pitchText?: string;
    advancedId?: string;
    advancedText?: string;
    legacyId?: string;
    section?: 'pitch' | 'question' | 'advanced';
  }): void {
    this.send({
      type: 'sales_playbook_update',
      data: {
        room_id: data.roomId,
        update_type: data.updateType,
        playbookName: data.playbookName,
        playbookDisplayName: data.playbookDisplayName,
        role: data.role,
        roleDisplayName: data.roleDisplayName,
        totalQuestionsAvailable: data.totalQuestionsAvailable,
        questionId: data.questionId,
        questionText: data.questionText,
        tags: data.tags,
        isAsked: data.isAsked,
        isChecked: data.isChecked,
        note: data.note,
        pitchId: data.pitchId,
        pitchText: data.pitchText,
        advancedId: data.advancedId,
        advancedText: data.advancedText,
        legacyId: data.legacyId,
        section: data.section
      }
    });
  }
}

export const recallWebSocketService = new RecallWebSocketService();
